import {createAction, handleActions} from 'redux-actions';
import {Api} from '../../api/api';
import {logout, onForbidden} from '../account/login';
import {fetchSubmitFormFailed, fetchSubmitFormSuccess} from '../common/submitForm';
import _ from 'lodash';

export const AVAILABLE_MANAGED_OPTIONS = {
    SELF_MANAGED: 'self_managed',
    BAVA_MANAGED: 'bava_managed',
};

//- Actions
export const fetchGetValidatedDomainsSuccess = createAction('FETCH_GET_VALIDATED_DOMAINS_SUCCESS');
export const fetchGetValidatedDomainsFailed = createAction('FETCH_GET_VALIDATED_DOMAINS_FAILED');

export const onFetchGetValidatedDomains = createAction(
    'FETCH_GET_VALIDATED_DOMAINS',
    (countryCode, name, domain, page) => Api.getValidatedDomains(countryCode, name, domain, page),
    () => ({
        apiCall: true,
        onSuccess: fetchGetValidatedDomainsSuccess,
        onFail: fetchGetValidatedDomainsFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const fetchGetValidatedDomains = (page) => (dispatch, getState) => {
    let countryCode = getValidatedDomainsCountryFilter(getState());
    let name = getValidatedDomainsNameFilter(getState());
    let domain = getValidatedDomainsDomainFilter(getState());

    dispatch(onFetchGetValidatedDomains(countryCode, name, domain, page));
}

export const fetchGetValidatedDomainsByCountry = (countryCode) => (dispatch, getState) => {
    let name = getValidatedDomainsNameFilter(getState());
    let domain = getValidatedDomainsDomainFilter(getState());

    dispatch(setValidatedDomainsCountryFilter(countryCode));
    dispatch(onFetchGetValidatedDomains(countryCode, name, domain));
}

export const fetchGetValidatedDomainsByNameAndDomain = (name, domain) => (dispatch, getState) => {
    let countryCode = getValidatedDomainsCountryFilter(getState());

    dispatch(setValidatedDomainsNameFilter(name));
    dispatch(setValidatedDomainsDomainFilter(domain));
    dispatch(onFetchGetValidatedDomains(countryCode, name, domain));
}

export const setValidatedDomainsCountryFilter = createAction('SET_VALIDATED_DOMAINS_COUNTRY_FILTER', countryCode => countryCode);

export const setValidatedDomainsNameFilter = createAction('SET_VALIDATED_DOMAINS_NAME_FILTER', name => name);

export const setValidatedDomainsDomainFilter = createAction('SET_VALIDATED_DOMAINS_DOMAIN_FILTER', domain => domain);

export const fetchValidatedDomainSuccess = () => (dispatch) => {
    dispatch(fetchGetValidatedDomains());
    dispatch(fetchSubmitFormSuccess());
}

export const onFetchAddValidatedDomain = createAction(
    'FETCH_ADD_VALIDATED_DOMAIN',
    (data, bavaManaged) => Api.addValidatedDomain(data, bavaManaged),
    () => ({
        apiCall: true,
        onSuccess: fetchValidatedDomainSuccess,
        onFail: fetchSubmitFormFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const onFetchEditValidatedDomain = createAction(
    'FETCH_ADD_VALIDATED_DOMAIN',
    (domain, data, bavaManaged) => Api.editValidatedDomain(domain, data, bavaManaged),
    () => ({
        apiCall: true,
        onSuccess: fetchValidatedDomainSuccess,
        onFail: fetchSubmitFormFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const onFetchRemoveValidatedDomain = createAction(
    'FETCH_REMOVE_VALIDATED_DOMAIN',
    (domain, data) => Api.removeValidatedDomain(domain, data),
    () => ({
        apiCall: true,
        onSuccess: fetchValidatedDomainSuccess,
        onFail: fetchSubmitFormFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

//- State
const initialState = {
    data: null,
    error: null,
    fetching: false,
    currentPage: null,
    totalPages: null,
    filterCountry: null,
    filterName: null,
    filterDomain: null,
};

//- Reducers
export default handleActions({

    FETCH_GET_VALIDATED_DOMAINS: (state) => {
        return {...state, error: null, fetching: true, currentPage: null, totalPages: null};
    },
    FETCH_GET_VALIDATED_DOMAINS_SUCCESS: (state, action) => {
        return {
            ...state,
            data: action.payload.content,
            error: null,
            fetching: false,
            currentPage: action.payload.number,
            totalPages: action.payload.totalPages
        };
    },
    FETCH_GET_VALIDATED_DOMAINS_FAILED: (state, action) => {
        return {
            ...state,
            error: action.payload,
            fetching: false
        };
    },
    SET_VALIDATED_DOMAINS_COUNTRY_FILTER: (state, action) => {
        return {
            ...state,
            filterCountry: action.payload
        }
    },
    SET_VALIDATED_DOMAINS_NAME_FILTER: (state, action) => {
        return {
            ...state,
            filterName: action.payload
        }
    },
    SET_VALIDATED_DOMAINS_DOMAIN_FILTER: (state, action) => {
        return {
            ...state,
            filterDomain: action.payload
        }
    }

}, initialState);

//- Selectors
export const getValidatedDomains = state => state.validatedDomains;

export const getValidatedDomainsCountryFilter = state => getValidatedDomains(state).filterCountry;

export const getValidatedDomainsNameFilter = state => getValidatedDomains(state).filterName;

export const getValidatedDomainsDomainFilter = state => getValidatedDomains(state).filterDomain;

export const getValidatedDomainsCurrentPage = state => getValidatedDomains(state).currentPage;

export const getValidatedDomainsTotalPages = state => getValidatedDomains(state).totalPages;

export const loadingValidatedDomains = state => getValidatedDomains(state).fetching;

export const getValidatedDomainsData = state => getValidatedDomains(state).data;

export const getValidatedDomainsFormattedData = state => {
    let data = getValidatedDomainsData(state);

    if (!data) {
        return null;
    }

    return _.map(data, validatedDomain => ({
        requestsNumber: validatedDomain.amount,
        orgType: validatedDomain.organization.type,
        orgName: validatedDomain.organization.name,
        orgDomain: validatedDomain.organization.domain,
        orgCountry: validatedDomain.organization.address.countryCode,
        orgCity: validatedDomain.organization.address.city,
        orgPostal: validatedDomain.organization.address.postal,
        orgStreet: validatedDomain.organization.address.street,
        fullyRegistered: validatedDomain.organization.fullyRegistered,
        contactName: validatedDomain.organization.contacts.name,
        contactEmail: validatedDomain.organization.contacts.email,
        contactPhone: validatedDomain.organization.contacts.phone,
        managedBy: _.isNil(validatedDomain.organization.managingOrganizationId) ? AVAILABLE_MANAGED_OPTIONS.SELF_MANAGED : AVAILABLE_MANAGED_OPTIONS.BAVA_MANAGED
    }))
};


// WEBPACK FOOTER //
// ./src/reducers/accreditation/validatedDomains.js